// SPDX-License-Identifier: Apache-2.0
import IGirafeContext from '../../context/icontext';
import { IBrainSerializer } from '../../state/brain/serialize';
import GlobeState from '../../state/globe';
import LayoutState, { CellContent, LayoutType } from '../../state/layout';
import LayoutSerializer from './layoutserializer';

/**
 * The layouts the display modes of the previous versions correspond to.
 *
 * <code>globe.display</code> was replaced by <code>state.layout</code>, but the states that were
 * shared before that change still contain it and have to keep working. Such a state never contains
 * a layout, so there is no risk of overwriting a layout that was deserialized before.
 */
const LEGACY_DISPLAYS: Record<string, Pick<LayoutState, 'type' | 'cells' | 'sizes'>> = {
  '2D': { type: '1x1', cells: ['map-2d'], sizes: { columns: [100], rows: [100] } },
  '3D': { type: '1x1', cells: ['map-3d'], sizes: { columns: [100], rows: [100] } },
  '2D/3D': { type: '2x1', cells: ['map-2d', 'map-3d'], sizes: { columns: [45, 55], rows: [100] } }
};

/** The state of the globe of the previous versions, which also contained the display mode. */
type LegacyGlobeState = Partial<GlobeState> & { display?: string };

export default class GlobeSerializer implements IBrainSerializer<GlobeState> {
  private readonly context: IGirafeContext;

  public constructor(context: IGirafeContext) {
    this.context = context;
  }

  private get globe() {
    return this.context.stateManager.state.globe;
  }

  public brainSerialize(globe: GlobeState): string {
    return JSON.stringify({
      shadows: globe.shadows,
      shadowsTimestamp: globe.shadowsTimestamp,
      camera: globe.camera
    });
  }

  public brainDeserialize(str: string): void {
    const parsedGlobe = JSON.parse(str) as LegacyGlobeState;
    const globe = this.globe;

    if (parsedGlobe.shadows !== undefined) {
      globe.shadows = parsedGlobe.shadows;
    }
    if (parsedGlobe.shadowsTimestamp) {
      globe.shadowsTimestamp = parsedGlobe.shadowsTimestamp;
    }
    globe.camera = parsedGlobe.camera ?? null;
    globe.loaded = false;

    this.deserializeLegacyDisplay(parsedGlobe.display);
  }

  /**
   * Applies the layout a display mode of the previous versions corresponds to.
   * TODO REG: Remove the legacy code when not used any more
   * */
  private deserializeLegacyDisplay(display?: string) {
    if (display === undefined) {
      return;
    }
    const legacyLayout = LEGACY_DISPLAYS[display];
    if (!legacyLayout) {
      console.warn(`${display} is not a known display mode. The shared display mode will be ignored.`);
      return;
    }
    new LayoutSerializer(this.context).applyLayout(
      legacyLayout.type as LayoutType,
      legacyLayout.cells as CellContent[],
      legacyLayout.sizes
    );
  }
}
